import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
} from "@mui/material";
import { getExpiryDate } from "data/functions";
import { ServiceSummary, User } from "data/interfaces";

interface ServicePaneProps {
  services: ServiceSummary[];
  selected: ServiceSummary | null;
  onSelect: (service: ServiceSummary) => void;
  users: User[];
}

export default function ServicePane({
  services,
  selected,
  onSelect,
  users,
}: ServicePaneProps) {
  const serviceGroups = Array.from(
    new Set(services.map((s) => s.serviceGroup))
  );

  return (
    <Box p={3}>
      <Box sx={{ fontWeight: "bold" }} pb={2} px={1}>
        <Grid container>
          <Grid item xs={8}>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <span>Service</span>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box display="flex" flexDirection="column" alignItems="flex-end">
              <span>Assigned / Users</span>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Stack spacing={2}>
        {serviceGroups.map((sg) => {
          const servicesInGroup = services
            .filter(
              (s) =>
                s.serviceGroup === sg && !(s.seatsTotal === 0 && s.oneTimeSeats)
            )
            .sort((a, b) => {
              if (a.name > b.name) {
                return 1;
              } else if (a.name < b.name) {
                return -1;
              } else if (
                a.nextLicenseExpiresOn !== undefined &&
                b.nextLicenseExpiresOn !== undefined
              ) {
                return new Date(a.nextLicenseExpiresOn) >
                  new Date(b.nextLicenseExpiresOn)
                  ? 1
                  : -1;
              } else {
                return 0;
              }
            });
          const serviceNames = servicesInGroup.map((a) => a.name);
          const duplicateNames = serviceNames.filter(
            (item, index) => serviceNames.indexOf(item) !== index
          );

          const serviceReferences = servicesInGroup.map(
            (s) => s.serviceReference
          );
          const usersInGroup = users.filter((u) =>
            u.services.some((s) =>
              serviceReferences.includes(s.serviceReference)
            )
          );

          return (
            <Paper key={sg}>
              <List dense disablePadding>
                <ListItem>
                  <ListItemText
                    disableTypography
                    sx={{ fontWeight: "bold", width: "100%" }}
                    primary={
                      <Grid container>
                        <Grid item xs={6}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-start"
                          >
                            <span>{sg}</span>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <span>
                              {usersInGroup.length} / {users.length}
                            </span>
                          </Box>
                        </Grid>
                      </Grid>
                    }
                  />
                </ListItem>
                <Divider />
                {servicesInGroup.map((s) => (
                  <ListItem
                    disablePadding
                    onClick={() => onSelect(s)}
                    key={s.licenseReference}
                  >
                    <ListItemButton
                      selected={
                        s.licenseReference === selected?.licenseReference
                      }
                    >
                      <ListItemText>
                        {s.name}
                        {s.licenseQualifier ? ` (${s.licenseQualifier})` : ""}
                        {duplicateNames.includes(s.name) && getExpiryDate(s)}
                      </ListItemText>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
